export type Coord = [number, number, number];

export type Geometry =
  | { type: 'Point'; coordinates: [number, number, number?] }
  | { type: 'MultiPoint'; coordinates: [number, number, number?][] }
  | { type: 'LineString'; coordinates: [number, number, number?][] }
  | { type: 'MultiLineString'; coordinates: [number, number, number?][][] }
  | { type: 'Polygon'; coordinates: [number, number, number?][][] }
  | { type: 'MultiPolygon'; coordinates: [number, number, number?][][][] };

export type VertexPath =
  | { type: 'Point' }
  | { type: 'LineString'; vertexIdx: number }
  | { type: 'MultiLineString'; lineIdx: number; vertexIdx: number }
  | { type: 'Polygon'; ringIdx: number; vertexIdx: number }
  | { type: 'MultiPolygon'; polyIdx: number; ringIdx: number; vertexIdx: number };

const EPS = 1e-9;
const isRingClosed = (ring: [number, number, number?][]) => {
  if (ring.length < 2) return false;
  const [x0, y0] = ring[0];
  const [xn, yn] = ring[ring.length - 1];
  return Math.abs(x0 - xn) < EPS && Math.abs(y0 - yn) < EPS;
};

const replaceInRing = (
  ring: [number, number, number?][],
  vertexIdx: number,
  next: Coord,
): [number, number, number?][] => {
  const out = ring.slice();
  out[vertexIdx] = next;
  if (isRingClosed(ring)) {
    if (vertexIdx === 0) out[ring.length - 1] = next;
    else if (vertexIdx === ring.length - 1) out[0] = next;
  }
  return out;
};

export const updateVertexAt = (geometry: Geometry, path: VertexPath, next: Coord): Geometry => {
  switch (path.type) {
    case 'Point': {
      if (geometry.type !== 'Point') return geometry;
      return { ...geometry, coordinates: next };
    }
    case 'LineString': {
      if (geometry.type !== 'LineString') return geometry;
      const coords = geometry.coordinates.slice();
      coords[path.vertexIdx] = next;
      return { ...geometry, coordinates: coords };
    }
    case 'MultiLineString': {
      if (geometry.type !== 'MultiLineString') return geometry;
      const lines = geometry.coordinates.slice();
      const line = lines[path.lineIdx].slice();
      line[path.vertexIdx] = next;
      lines[path.lineIdx] = line;
      return { ...geometry, coordinates: lines };
    }
    case 'Polygon': {
      if (geometry.type !== 'Polygon') return geometry;
      const rings = geometry.coordinates.slice();
      rings[path.ringIdx] = replaceInRing(rings[path.ringIdx], path.vertexIdx, next);
      return { ...geometry, coordinates: rings };
    }
    case 'MultiPolygon': {
      if (geometry.type !== 'MultiPolygon') return geometry;
      const polys = geometry.coordinates.slice();
      const rings = polys[path.polyIdx].slice();
      rings[path.ringIdx] = replaceInRing(rings[path.ringIdx], path.vertexIdx, next);
      polys[path.polyIdx] = rings;
      return { ...geometry, coordinates: polys };
    }
  }
};
